import { RemarksInt } from '@app/interfaces/reservation-aws.interface';

/**
 * Fuente de verdad del cobro real de seguros CHUBB.
 *
 * El status crudo del ancillary en Sabre NO es confiable para saber si un seguro
 * se pagó:
 *  - Un producto solo "Agregado" (PUT al carrito) queda en ONHOLD sin cobro.
 *  - Un producto realmente pagado puede seguir mostrándose ONHOLD (el EMD tarda
 *    en reflejarse) en vez de PAID.
 *
 * La evidencia contable del cobro son los remarks REG con formato:
 *   TBCCB/CHUBB/PAID/K/MX/<PRODUCT>/<PAX>/<ETICKET>/<AMOUNT>
 * p. ej.:
 *   TBCCB/CHUBB/PAID/K/MX/SXC/1.1/1392167375120/290.00
 *
 * Donde <PAX> viene como "1.1" / "2.1" (sin ceros a la izquierda), mientras que
 * en el pasajero el identificador es nameRefNumber = "01.01" / "02.01".
 */

const CHUBB_PAID_REMARK_PREFIX = 'TBCCB/CHUBB/PAID/';

/** Normaliza "01.01" o "1.1" a una forma canónica "1.1" para comparar. */
export function normalizePaxRef(ref: string): string {
  return (ref ?? '')
    .split('.')
    .map((part) => String(parseInt(part, 10)))
    .join('.');
}

/**
 * Extrae del PNR el mapa de seguros CHUBB realmente pagados:
 *   { productCode -> Set<paxRefNormalizado> }
 * a partir de los remarks TBCCB/CHUBB/PAID.
 */
export function getPaidChubbInsuranceMap(
  remarks: RemarksInt[] | undefined
): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>();
  (remarks ?? []).forEach((remark) => {
    const text = (remark?.text ?? '').trim();
    if (!text.startsWith(CHUBB_PAID_REMARK_PREFIX)) return;
    // TBCCB / CHUBB / PAID / K / MX / <PRODUCT> / <PAX> / <ETICKET> / <AMOUNT>
    const parts = text.split('/');
    const productCode = parts[5];
    const paxRef = parts[6];
    if (!productCode || !paxRef) return;
    const normalized = normalizePaxRef(paxRef);
    const paxRefs = result.get(productCode) ?? new Set<string>();
    paxRefs.add(normalized);
    result.set(productCode, paxRefs);
  });
  return result;
}

/** Estructura mínima de un ancillary CHUBB para evaluar el cobro. */
interface ChubbAncillaryLike {
  code?: string;
  rficSubcode?: string;
  status?: string;
  emd?: string;
}

/**
 * Indica si un pasajero (por nameRefNumber, p. ej. "01.01") tiene el producto
 * CHUBB indicado realmente pagado según los remarks del PNR.
 */
export function isChubbInsurancePaid(
  remarks: RemarksInt[] | undefined,
  productCode: string,
  nameRefNumber: string
): boolean {
  const map = getPaidChubbInsuranceMap(remarks);
  const paxRefs = map.get(productCode);
  if (!paxRefs) return false;
  return paxRefs.has(normalizePaxRef(nameRefNumber));
}

/**
 * Indica si un ancillary CHUBB representa un cobro REAL: status PAID con un EMD
 * emitido. El EMD (documento fiscal) es lo que distingue un pago confirmado de un
 * ONHOLD reservado por "Agregar" al carrito, que nunca trae EMD.
 */
export function hasPaidChubbAncillary(
  ancillaries: ChubbAncillaryLike[] | undefined,
  productCode: string
): boolean {
  return (ancillaries ?? []).some((a) => {
    const matchesCode = a?.code === productCode || a?.rficSubcode === productCode;
    const isPaid = (a?.status ?? '').toString().toUpperCase() === 'PAID';
    const hasEmd = !!a?.emd && String(a.emd).trim().length > 0;
    return matchesCode && isPaid && hasEmd;
  });
}

/**
 * Combina las dos evidencias de cobro de un seguro CHUBB para un pasajero:
 * remark TBCCB/CHUBB/PAID del PNR, o ancillary PAID con EMD emitido.
 */
export function isChubbInsurancePaidForPassenger(
  remarks: RemarksInt[] | undefined,
  ancillaries: ChubbAncillaryLike[] | undefined,
  productCode: string,
  nameRefNumber: string
): boolean {
  return (
    isChubbInsurancePaid(remarks, productCode, nameRefNumber) ||
    hasPaidChubbAncillary(ancillaries, productCode)
  );
}
